<template>
	<nav class="bg-white sidebar">
		<div class="dossier-selector full-width">
			<div class="dossier-name">
				<span>{{ dossierStore?.selectedDossier?.name }}</span>
			</div>
		</div>
		<AppButton v-if="false" variant="secondary">Bekijk als klant</AppButton>
		<ul class="items">
			<li v-if="userStore.role === 'customer'">
				<RouterLink :to="{ name: 'Home' }" class="menu-item only-exact-active">
					<SidebarMenuItem icon="home_storage" label="Documenten" />
				</RouterLink>
			</li>
			<li v-if="userStore.role !== 'customer'">
				<RouterLink :to="{ name: 'Inventarisation' }" class="menu-item">
					<SidebarMenuItem icon="person_search" label="Inventarisatie" />
				</RouterLink>
			</li>
			<li>
				<RouterLink :to="{ name: 'Financiering' }" class="menu-item">
					<SidebarMenuItem icon="calculate" label="Financiering" />
				</RouterLink>
			</li>
			<li v-if="userStore.role !== 'customer' && retirementAdviceEnabled">
				<RouterLink :to="{ name: 'Advies' }" class="menu-item">
					<SidebarMenuItem icon="spa" label="Advies" />
				</RouterLink>
			</li>

			<li v-if="userStore.role !== 'customer'">
				<RouterLink :to="{ name: 'Documenten' }" class="menu-item">
					<SidebarMenuItem icon="home_storage" label="Documenten" />
				</RouterLink>
			</li>
			<li v-if="userStore.role !== 'customer'">
				<RouterLink
					:to="{ name: 'LoanApplicationOverviewPage' }"
					class="menu-item"
				>
					<SidebarMenuItem label="Aanvragen">
						<template #icon>
							<HdnIcon class="sidebar-icon" />
						</template>
					</SidebarMenuItem>
				</RouterLink>
				<ul class="ml-4 border-l">
					<li
						v-for="loanApplication in loanApplications"
						:key="loanApplication.id"
						class="ml-1"
					>
						<RouterLink
							:to="{
								name: 'LoanApplicationDetailsPage',
								params: { loanApplicationId: loanApplication.id },
							}"
							class="menu-item"
						>
							<SidebarMenuItem
								:label="
									getLenderOption(
										loanApplication.hdnDossier?.providerCode ?? undefined,
									)?.label ??
									loanApplication.hdnDossier?.provider?.label ??
									splitHdnValue(
										loanApplication.hdnDossier?.provider?.name,
									)[1] ??
									''
								"
							>
								<template #icon>
									<img
										v-if="
											getLenderOption(
												loanApplication.hdnDossier?.providerCode ?? undefined,
											)?.icon
										"
										class="w-5 h-4 object-contain"
										:src="`/logos/${getLenderOption(loanApplication.hdnDossier?.providerCode ?? undefined)?.icon}`"
									/>
								</template>
							</SidebarMenuItem>
						</RouterLink>
					</li>
				</ul>
			</li>
			<li v-if="userStore.role !== 'customer'">
				<RouterLink :to="{ name: 'Instellingen' }" class="menu-item">
					<SidebarMenuItem icon="settings" label="Instellingen" />
				</RouterLink>
			</li>
		</ul>
		<div class="sidebar-footer">
			<button
				v-if="userStore.role !== 'customer'"
				class="feedback-button"
				@click="openFeedback"
			>
				<span class="ht-icon sidebar-icon">feedback</span>
				<span class="menu-item-label">Geef feedback</span>
			</button>
			<span class="version-label">v{{ appVersion }}</span>
		</div>
	</nav>
</template>

<script setup lang="ts">
import * as Sentry from "@sentry/vue";
import { useFeatureFlag } from "@/composables/useFeatureFlag";
import { useProviderLenders } from "@/composables/useProviderLenders";
import { useDossierStore } from "@/store/useDossierStore";
import HdnIcon from "@/assets/icons/hdn.svg";
import { useUserStore } from "@/store/useUserStore";
import { useQuery } from "@/composables/useZero";
import { queries } from "@loanlink-nl/zero";
import { splitHdnValue } from "@/helpers/hdnHelpers";

const dossierStore = useDossierStore();
const userStore = useUserStore();
const retirementAdviceEnabled = useFeatureFlag("retirement-advice");

const { data: loanApplications } = useQuery(() =>
	queries.loanApplications.forDossier({
		dossierId: dossierStore.selectedDossier?.id ?? "",
		orderField: "createdAt",
		order: "desc",
	}),
);

const { getLenderOption } = useProviderLenders();

const appVersion = __APP_VERSION__;

async function openFeedback() {
	const feedback = Sentry.getFeedback();
	if (!feedback) return;
	const form = await feedback.createForm();
	form.appendToDom();
	form.open();
}
</script>

<style lang="scss">
@use "@/style/mixins.scss" as mixins;

.sidebar {
	--horizontal-padding: 16px;
	--closed-h-padding: 0;
	--closed-item-h-padding: 15px;
	--item-border-size: 0;
	--collapse-duration: 0.4s;
	--logo-icon-size: 30px;

	flex: 0 0 220px;
	height: 100%;
	transition-duration: var(--collapse-duration);
	overflow: hidden;
	display: flex;
	flex-direction: column;

	z-index: 3;

	border-right: 1px solid var(--border);

	.dossier-selector {
		position: relative;

		font-size: 0.85rem;
		font-weight: 500;

		display: flex;
		flex-direction: column;

		width: 100%;
		background: none;
		border: none;

		padding: 0.75rem 1.25rem;
		padding-right: 0.5rem;
		border-bottom: 1px solid var(--border);

		text-decoration: none;
		color: inherit;

		.dossier-name {
			display: flex;
			flex-direction: column;
			overflow: hidden;

			span {
				overflow: hidden;
				text-overflow: ellipsis;
				white-space: nowrap;
			}
		}
	}

	> * {
		margin-left: 0.75rem;
		margin-right: 0.75rem;
	}

	.full-width {
		margin: 0;
	}

	.items {
		padding: 0.5rem 0;
		list-style: none;
		display: flex;
		flex-direction: column;
		font-size: 0.85em;
		font-weight: 500;
		gap: 0.5rem;

		.menu-item {
			color: var(--text-muted);

			padding: 0.25rem 0.45rem;
			gap: 0.5rem;
			border-radius: var(--border-radius);

			cursor: pointer;

			display: flex;
			flex-direction: row;
			align-items: center;
			position: relative;

			text-decoration: none;

			.sidebar-icon {
				color: var(--text-secondary);
			}

			&:hover {
				background: var(--background-primary-hover);
			}

			&.router-link-exact-active,
			&.router-link-active:not(.only-exact-active) {
				color: var(--text-primary);

				.sidebar-icon {
					color: var(--accent-primary);
				}
			}

			.menu-item-label {
				display: inline-block;
				text-overflow: ellipsis;
				overflow: hidden;
				white-space: nowrap;
			}
		}
	}

	.sidebar-footer {
		flex-grow: 1;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: flex-end;
		padding-bottom: 1.25rem;

		.feedback-button {
			display: flex;
			flex-direction: row;
			align-items: center;
			gap: 0.5rem;
			width: 100%;
			padding: 0.25rem 0.45rem;
			border-radius: var(--border-radius);
			border: none;
			background: none;
			cursor: pointer;
			color: var(--text-muted);
			font-size: 0.85em;
			font-weight: 500;
			text-align: left;

			&:hover {
				background: var(--background-primary-hover);
			}
		}

		.version-label {
			font-size: 0.8rem;
			font-weight: 600;
			color: var(--text-muted);
		}
	}

	@media screen and (max-width: var(--mobile-size)) {
		border-right: none;
		border-bottom: 1px solid var(--border);
		width: 100%;

		.items {
			.menu-item {
				padding: 0.5rem;
			}
		}

		.logo-wrap {
			margin: 0 12px;

			.hamburger-menu {
				display: flex;
			}
		}

		&.closed {
			width: 0;
		}

		.sidebar-footer {
			display: none;
		}
	}
}

.dossier-selector-dropdown {
	position: absolute;
	left: 9px;
	z-index: 9;
	margin: 10px 0;

	background: white;
	border: 1px solid var(--border);
	border-radius: var(--border-radius);
	box-shadow: 0px 3px 12px rgba(0, 0, 0, 0.09);

	overflow: auto;
	max-height: calc(100% - 20px);

	font-size: 0.85rem;
	font-weight: 500;

	scrollbar-width: none;
	&::-webkit-scrollbar {
		display: none;
	}

	ul {
		list-style-type: none;
		padding: 0;

		li {
			padding: 10px;
			pointer-events: auto;
			cursor: pointer;
			padding-right: 30px;
			white-space: nowrap;
			position: relative;

			&.current {
				&::after {
					position: absolute;
					right: 3px;
					content: "done";

					@include mixins.ht-icon;

					font-size: 1.5rem;
					color: var(--text-secondary);
					top: 50%;
					transform: translateY(-50%);
				}
			}

			&:not(:last-child) {
				border-bottom: 1px solid var(--border);
			}

			&:hover {
				background: var(--background-primary-hover);
			}
		}
	}
}
</style>
